import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Star, Check, Info, Upload, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import kitchenStationService from "../../../services/kitchenStationService";
import categoryService from "../../../services/categoryService";
import { itemService } from '../../../services/menuitemService';
import toast from 'react-hot-toast';
import useTax from "../../../hooks/useTax";


const TABS = [
  { key: 'details', label: 'Details' },
  { key: 'tax', label: 'Pricing & Tax' },
  { key: 'options', label: 'Options' },
  { key: 'variants', label: 'Variants' },
  { key: 'image', label: 'Image' },
];

const FIELD_TAB_MAP = {
  name: 'details',
  category: 'details',
  basePrice: 'tax',
  hsn: 'tax',
};

export default function MenuForm({
  item,
  categories: initialCategories,
  onSave,
  onClose,
  onImageUpload,
  onCategoryCreated,
  serverError = null,
  onClearServerError,
  existingItems = [],
}) {
  const [categories, setCategories] = useState(initialCategories);
  const [kitchenStations, setKitchenStations] = useState([]);
  const [loadingStations, setLoadingStations] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [validationErrors, setValidationErrors] = useState({});
  // Per-field messages, keyed like validationErrors but holding text rather than a
  // boolean. Sourced from the local duplicate check and from the API's 409 reply.
  const [fieldMessages, setFieldMessages] = useState({});

  // Inline category creation state
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Tax dropdown data
  const { taxes } = useTax();
  const activeTaxes = taxes.filter((t) => t.isActive);

  const [formData, setFormData] = useState({
    name: '',
    category: categories[0]?.name || '',
    categoryId: categories[0]?.id || '',
    kitchenStation: '',
    kitchenStationId: '',
    shortcutKey: '',
    basePrice: '',
    preparationTime: '',
    description: '',
    isActive: true,
    isFavorite: false,
    customizable: true,
    isVeg: true,
    variants: [],
    // Tax fields
    taxId: null,
    inclusiveTaxRate: true,
    // Image URL
    imageUrl: null,
    // HSN field — 996331 is the default SAC for restaurant services
    hsn: '996331',
    // Service-mode price overrides
    takeawayPrice: '',
    deliveryPrice: '',
    // Open item flags
    isOpenItem: false,
    isNameEditable: false,
    // Finished item (direct inventory deduction)
    isFinishedItem: false,
    // New optional fields
    isTopping: false,
    // New optional fields (string, from backend)
    itemCode: '',
    barcode: '',
  });

  // Keep local categories in sync with parent
  useEffect(() => {
    setCategories(initialCategories);
  }, [initialCategories]);

  //handles active/inactive states
  const activeCategories = categories.filter(cat => cat.isActive);

  const categoryOptions = React.useMemo(() => {
    if (!item) return activeCategories;

    const currentCategory = categories.find(c => c.id === item.categoryId);
    if (currentCategory && !currentCategory.isActive) {
      return [currentCategory, ...activeCategories];
    }
    return activeCategories;
  }, [categories, item]);

  const activeKitchenStations = kitchenStations.filter(st => st.isActive);

  const kitchenStationOptions = React.useMemo(() => {
    if (!item) return activeKitchenStations;

    const currentStation = kitchenStations.find(
      st => st.id === item.kitchenStationId
    );
    if (currentStation && !currentStation.isActive) {
      return [currentStation, ...activeKitchenStations];
    }
    return activeKitchenStations;
  }, [kitchenStations, item]);

  // Track deleted variant IDs to send to backend for deletion
  const [deletedVariantIds, setDeletedVariantIds] = useState([]);

  const fetchKitchenStations = async () => {
    try {
      setLoadingStations(true);
      const data = await kitchenStationService.getAll();
      setKitchenStations(data || []);
    } catch (error) {
      console.error("Failed to fetch kitchen stations:", error);
    } finally {
      setLoadingStations(false);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;

    try {
      setCreatingCategory(true);
      const created = await categoryService.create({ name, isActive: true });
      toast.success(`Category "${name}" created`);

      // Update local categories list
      const updatedCategories = await categoryService.getAll();
      setCategories(updatedCategories || []);

      // Auto-select the new category
      setFormData(prev => ({
        ...prev,
        category: created.name,
        categoryId: created.id,
      }));

      // Clear validation error on category
      if (validationErrors.category) {
        setValidationErrors(prev => {
          const next = { ...prev };
          delete next.category;
          return next;
        });
      }

      // Notify parent so its category list stays in sync
      if (onCategoryCreated) onCategoryCreated();

      // Reset inline form
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to create category';
      toast.error(msg);
    } finally {
      setCreatingCategory(false);
    }
  };

  useEffect(() => {
    fetchKitchenStations();
  }, []);

  // Load form data from item (edit mode) or set defaults (create mode)
  useEffect(() => {
    // Reset deleted variant IDs when opening form
    setDeletedVariantIds([]);

    if (item) {
      setFormData({
        name: item.name || '',
        category: item.categoryName || categories[0]?.name || '',
        categoryId: item.categoryId || categories[0]?.id || '',
        kitchenStation: item.kitchenStationName || '',
        kitchenStationId: item.kitchenStationId || '',
        shortcutKey: item.shortcutKey?.toString() || '',
        basePrice: item.basePrice?.toString() || '',
        description: item.description || '',
        isActive: item.isActive !== undefined ? item.isActive : true,
        isFavorite: item.isFavourite || false,
        customizable: item.isCustomizable !== undefined ? item.isCustomizable : true,
        preparationTime: item.preparationTime?.toString() || '',
        isVeg: item.isVeg !== undefined ? item.isVeg : true,
        // Tax fields
        taxId: item.taxId || null,
        inclusiveTaxRate: item.inclusiveTaxRate !== false,
        imageUrl: item.imageUrl || null,
        // HSN field
        hsn: item.hsn || '996331',

        itemCode: item.itemCode || '',
        barcode: item.barcode || '',
        // Service-mode price overrides
        takeawayPrice: item.takeawayPrice?.toString() || '',
        deliveryPrice: item.deliveryPrice?.toString() || '',
        // Open item flags
        isOpenItem: item.isOpenItem ?? false,
        isNameEditable: item.isNameEditable ?? false,
        // Finished item
        isFinishedItem: item.isFinishedItem ?? false,
         // Topping flag
        isTopping: item.markedAsToppings ?? false,
        // Effective tax (read-only display)
        effectiveTaxId: item.effectiveTaxId || null,
        effectiveTaxName: item.effectiveTaxName || '',
        effectiveTaxRate: item.effectiveTaxRate || null,
        effectiveTaxCgstRate: item.effectiveTaxCgstRate || null,
        effectiveTaxSgstRate: item.effectiveTaxSgstRate || null,
        taxSource: item.taxSource || '',
        variants:
          item.variants && item.variants.length > 0
            ? item.variants.map((v, index) => ({
                id: v.id,
                tempId: v.id || `temp_${Date.now()}_${index}`,
                variantName: v.variantName || '',
                variantPrice: v.variantPrice?.toString() || '',
                takeawayPrice: v.takeawayPrice?.toString() || '',
                deliveryPrice: v.deliveryPrice?.toString() || '',
                isAvailable: v.isAvailable !== undefined ? v.isAvailable : true,
              }))
            : [],
      });

      // Use the public URL endpoint to display the image
      if (item.imageUrl) {
        setImagePreview(itemService.getPhotoUrl(item.imageUrl));
      } else if (item.image) {
        setImagePreview(item.image);
      }
    }
  }, [item, categories]);

  // Auto-select first kitchen station for new items (create mode only)
  useEffect(() => {
    if (!item && kitchenStations.length > 0) {
      setFormData(prev => ({
        ...prev,
        kitchenStation: kitchenStations[0].name,
        kitchenStationId: kitchenStations[0].id,
      }));
    }
  }, [item, kitchenStations]);

  // Auto-fill variant price when basePrice changes
  useEffect(() => {
    if (formData.basePrice) {
      setFormData(prev => ({
        ...prev,
        variants: prev.variants.map((variant, index) => {
          if (index === 0 && (!variant.variantPrice || variant.variantPrice === '')) {
            return { ...variant, variantPrice: prev.basePrice };
          }
          return variant;
        }),
      }));
    }
  }, [formData.basePrice]);

  // A server-side field error (e.g. duplicate name) arrives after the save attempt:
  // surface it on the right tab, focus the field and select the text so the user can
  // type straight over it.
  useEffect(() => {
    if (!serverError?.field) return;

    setValidationErrors(prev => ({ ...prev, [serverError.field]: true }));
    setFieldMessages(prev => ({ ...prev, [serverError.field]: serverError.message }));

    const targetTab = FIELD_TAB_MAP[serverError.field] || 'details';
    setActiveTab(targetTab);

    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-field="${serverError.field}"]`);
      el?.focus();
      el?.select?.();
    }, 50);
    return () => clearTimeout(timer);
  }, [serverError]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user types
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (fieldMessages[field]) {
      setFieldMessages(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      // Let the parent drop the server error too, so it can't be re-applied.
      onClearServerError?.();
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) {
      toast.error('Please select an image first');
      return;
    }

    if (!item?.id) {
      toast.error('Please save the menu item first before uploading an image');
      return;
    }

    try {
      setUploadingImage(true);

      console.log('Uploading image for item:', item.id);

      const response = await itemService.uploadPhoto(item.id, imageFile);

      console.log('Upload API Response:', response);

      // Response format: { success: true, imageUrl: "/api/uploads/menu-items/filename", filename: "filename" }
      if (response && response.success) {
        toast.success('Image uploaded successfully');
        setImageFile(null);

        // Update formData with the filename (this gets saved to database)
        // Note: Backend's enrichWithEffectiveTax will convert filename to full URL when loading
        setFormData(prev => ({
          ...prev,
          imageUrl: response.filename
        }));

        // Use the full URL path from response for preview (add timestamp to bust cache)
        const previewUrl = itemService.getPhotoUrl(response.imageUrl) + '?t=' + Date.now();
        setImagePreview(previewUrl);

        console.log('Image uploaded - filename:', response.filename, 'preview URL:', previewUrl);
      } else {
        toast.error(response?.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    // Explicitly set imageUrl to null - this will be sent in the update payload
    setFormData(prev => ({
      ...prev,
      imageUrl: null
    }));

    // Show confirmation that image will be removed on save
    toast.success('Image will be removed when you save');
  };

  const handleAddVariant = () => {
    const newVariant = {
      tempId: `temp_${Date.now()}`,
      variantName: '',
      variantPrice: formData.basePrice || '',
      takeawayPrice: '',
      deliveryPrice: '',
      isAvailable: true,
    };
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant],
    }));
  };

  const handleRemoveVariant = (tempId) => {
    // Find the variant being removed
    const variantToRemove = formData.variants.find(v => v.tempId === tempId);

    // If it has a database ID, track it for deletion
    if (variantToRemove && variantToRemove.id) {
      setDeletedVariantIds(prev => [...prev, variantToRemove.id]);
    }

    // Remove from form state
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter(v => v.tempId !== tempId),
    }));
  };

  const handleVariantChange = (tempId, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v =>
        v.tempId === tempId ? { ...v, [field]: value } : v
      ),
    }));
  };

  const fillTestData = () => {
    const names = ['Butter Chicken', 'Paneer Tikka', 'Veg Biryani', 'Masala Dosa', 'Chicken Shawarma', 'Dal Makhani', 'Hakka Noodles', 'Tandoori Roti'];
    const descriptions = ['Chef special', 'House favorite', 'Freshly prepared', 'Traditional recipe', 'Spicy & flavorful'];
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const price = (Math.floor(Math.random() * 40) + 5) * 10; // 50–450 in steps of 10
    const isVeg = Math.random() > 0.5;

    const cat = categoryOptions.length > 0 ? pick(categoryOptions) : null;
    const station = kitchenStationOptions.length > 0 ? pick(kitchenStationOptions) : null;

    // Generate a random test itemCode and qrCode
    const randomSuffix = Math.floor(Math.random() * 9000 + 1000); // 4-digit
    const testItemCode = `ITM-${randomSuffix}`;
    const testBarcode = `BC${Date.now().toString().slice(-8)}`;

    setFormData(prev => ({
      ...prev,
      name: pick(names),
      description: pick(descriptions),
      category: cat?.name || prev.category,
      categoryId: cat?.id || prev.categoryId,
      kitchenStation: station?.name || prev.kitchenStation,
      kitchenStationId: station?.id || prev.kitchenStationId,
      basePrice: price.toString(),
      takeawayPrice: Math.round(price * 1.05).toString(),
      deliveryPrice: Math.round(price * 1.15).toString(),
      preparationTime: (Math.floor(Math.random() * 25) + 5).toString(),
      shortcutKey: Math.floor(Math.random() * 9 + 1).toString(),
      isActive: true,
      isFavorite: Math.random() > 0.7,
      isVeg,
      hsn: '996331',
      // New optional fields
      itemCode: testItemCode,
      barcode: testBarcode,
      variants: [
        { tempId: `temp_${Date.now()}_0`, variantName: 'Half', variantPrice: Math.round(price * 0.6).toString(), takeawayPrice: Math.round(price * 0.65).toString(), deliveryPrice: Math.round(price * 0.7).toString(), isAvailable: true },
        { tempId: `temp_${Date.now()}_1`, variantName: 'Full', variantPrice: price.toString(), takeawayPrice: Math.round(price * 1.05).toString(), deliveryPrice: Math.round(price * 1.15).toString(), isAvailable: true },
      ],
    }));
    setValidationErrors({});
    setActiveTab('details');
  };

  // Handle submit with manual validation for tabbed fields
  const handleSubmit = (e) => {
    e.preventDefault();

    // Check required fields manually since hidden tabs bypass HTML5 validation
    const errors = {};
    for (const [field, tab] of Object.entries(FIELD_TAB_MAP)) {
      // Skip basePrice validation for open items (price entered at POS)
      if (field === 'basePrice' && formData.isOpenItem) continue;
      if (!formData[field] || formData[field].toString().trim() === '') {
        errors[field] = true;
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setFieldMessages({});
      // Switch to the tab of the first invalid field
      const firstInvalidField = Object.keys(errors)[0];
      const targetTab = FIELD_TAB_MAP[firstInvalidField];
      setActiveTab(targetTab);
      setTimeout(() => {
        document.querySelector(`[data-field="${firstInvalidField}"]`)?.focus();
      }, 50);
      return;
    }

    // Catch a duplicate name before the round-trip so the user gets an instant answer.
    // Only fires on an exact case-insensitive hit in the already-loaded list (excluding
    // the item being edited), so it cannot produce a false positive; anything it misses
    // the API still rejects with 409.
    const trimmedName = formData.name.trim();
    const duplicate = existingItems.find(
      (existing) =>
        existing?.name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
        existing.id !== item?.id
    );

    if (duplicate) {
      setValidationErrors(prev => ({ ...prev, name: true }));
      setFieldMessages(prev => ({
        ...prev,
        name: `A menu item named "${duplicate.name}" already exists. Please choose a different name.`,
      }));
      setActiveTab('details');
      setTimeout(() => {
        const el = document.querySelector('[data-field="name"]');
        el?.focus();
        el?.select?.();
      }, 50);
      return;
    }

    const dataToSave = {
      // Send the trimmed name — the API trims before its duplicate check, so an
      // untrimmed value here would just come back rejected.
      name: trimmedName,
      description: formData.description,
      category: formData.category,
      categoryId: formData.categoryId,
      kitchenStation: formData.kitchenStation,
      kitchenStationId: formData.kitchenStationId,
      shortcutKey: parseInt(formData.shortcutKey) || 0,
      basePrice: parseFloat(formData.basePrice) || 0,
      isActive: formData.isActive,
      isFavourite: formData.isFavorite,
      isVeg: formData.isVeg,
      isCustomizable: formData.customizable,
      preparationTime: parseInt(formData.preparationTime) || 0,
      // Tax fields
      taxId: formData.taxId || null,
      inclusiveTaxRate: formData.inclusiveTaxRate !== false,
      imageUrl: formData.imageUrl,
      // HSN field
      hsn: formData.hsn,
      // New optional fields — send null when empty rather than an empty string
      itemCode: formData.itemCode?.trim() || null,  // new field for itemCode
      barcode: formData.barcode?.trim() || null,   // new field for barcode
      // Service-mode price overrides (null if empty = use basePrice)
      takeawayPrice: formData.takeawayPrice ? parseFloat(formData.takeawayPrice) : null,
      deliveryPrice: formData.deliveryPrice ? parseFloat(formData.deliveryPrice) : null,
      // Open item flags
      isOpenItem: formData.isOpenItem ?? false,
      isNameEditable: formData.isNameEditable ?? false,
      // Finished item
      isFinishedItem: formData.isFinishedItem ?? false,
       // Topping flag
      markedAsToppings: formData.isTopping ?? false,
      variants: formData.variants.map((v) => {
        const variant = {
          variantName: v.variantName || '',
          variantPrice: parseFloat(v.variantPrice) || 0,
          takeawayPrice: v.takeawayPrice ? parseFloat(v.takeawayPrice) : null,
          deliveryPrice: v.deliveryPrice ? parseFloat(v.deliveryPrice) : null,
          isAvailable: v.isAvailable,
        };

        if (v.id !== undefined && v.id !== null) {
          variant.id = typeof v.id === 'number' ? v.id : parseInt(v.id);
        }

        return variant;
      }),
      tags: [],
      calories: null,
      ingredients: [],
      allergens: [],
      customizationOptions: [],
      deletedVariantIds: deletedVariantIds,
    };

    onSave(dataToSave);
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-white border rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all ${
      validationErrors[field] ? 'border-red-400 ring-2 ring-red-100' : 'border-slate-200'
    }`;

  // ---------- Tab Content Renderers ----------

  const renderDetailsTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Item Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            data-field="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={inputClass('name')}
            placeholder="e.g., Margherita Pizza"
            aria-invalid={!!validationErrors.name}
            aria-describedby={fieldMessages.name ? 'menu-name-error' : undefined}
          />
          {fieldMessages.name && (
            <p id="menu-name-error" className="mt-1.5 flex items-start gap-1.5 text-xs text-red-600">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              <span>{fieldMessages.name}</span>
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Category <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <select
              data-field="category"
              value={formData.category}
              onChange={(e) => {
                const selectedCat = categories.find(cat => cat.name === e.target.value);
                handleChange('category', e.target.value);
                handleChange('categoryId', selectedCat?.id || '');
              }}
              className={`flex-1 ${inputClass('category')}`}
            >
              {categoryOptions.map(cat => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}{!cat.isActive ? ' (Inactive)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowNewCategory(prev => !prev)}
              className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all flex items-center gap-1 ${
                showNewCategory
                  ? 'bg-slate-100 border-slate-300 text-slate-600'
                  : 'bg-teal-600 border-teal-600 text-white hover:bg-teal-700'
              }`}
              title={showNewCategory ? 'Cancel' : 'Add new category'}
            >
              {showNewCategory ? <X size={16} /> : <Plus size={16} />}
            </button>
          </div>

          {/* Inline new category form */}
          {showNewCategory && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateCategory();
                  }
                  if (e.key === 'Escape') {
                    setShowNewCategory(false);
                    setNewCategoryName('');
                  }
                }}
                placeholder="New category name"
                className="flex-1 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition-all"
                autoFocus
                disabled={creatingCategory}
              />
              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={creatingCategory || !newCategoryName.trim()}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {creatingCategory ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                {creatingCategory ? 'Creating...' : 'Create'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Description
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
            placeholder="Optional description"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Kitchen Station
          </label>
          <select
            value={formData.kitchenStation}
            onChange={(e) => {
              const selectedStation = kitchenStations.find(st => st.name === e.target.value);
              handleChange('kitchenStation', e.target.value);
              handleChange('kitchenStationId', selectedStation?.id || '');
            }}
            disabled={loadingStations}
            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all disabled:bg-gray-50"
          >
            {loadingStations ? (
              <option>Loading...</option>
            ) : kitchenStationOptions.length > 0 ? (
              kitchenStationOptions.map(st => (
                <option key={st.id} value={st.name}>
                  {st.name}{!st.isActive ? ' (Inactive)' : ''}
                </option>
              ))
            ) : (
              <option value="">No kitchen stations available</option>
            )}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Preparation Time <span className="text-slate-500">(min)</span>
          </label>
          <input
            type="number"
            value={formData.preparationTime}
            onChange={(e) => handleChange('preparationTime', e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
            placeholder="e.g., 12"
            min="0"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
  <div>
    <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
      Item Code <span className="text-slate-400 normal-case">(optional)</span>
    </label>
    <input
      type="text"
      value={formData.itemCode}
      onChange={(e) => handleChange('itemCode', e.target.value)}
      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
      placeholder="e.g., ITM-1042"
    />
  </div>

  <div>
  <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
    Barcode <span className="text-slate-400 normal-case">(optional)</span>
  </label>
  <input
    type="text"
    value={formData.barcode}
    onChange={(e) => handleChange('barcode', e.target.value)}
    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
    placeholder="Scan or enter barcode"
  />
</div>
</div>
    </div>
  );

  const renderTaxTab = () => (
    <div className="grid grid-cols-2 gap-6 h-full">
      {/* ── Left: Pricing ── */}
      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pb-2 border-b border-slate-200">
          Pricing
        </h3>

        {/* Dine-In (Base) */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
            Dine-In (Base Price) <span className="text-red-500">*</span>
          </label>
          <div className={`flex items-center bg-white border rounded-lg px-3 py-2.5 transition-all focus-within:ring-2 focus-within:ring-slate-100 ${
            validationErrors.basePrice ? 'border-red-400 ring-2 ring-red-100' : 'border-slate-200 focus-within:border-slate-400'
          }`}>
            <span className="text-slate-400 font-medium text-sm mr-2">₹</span>
            <input
              type="number"
              data-field="basePrice"
              value={formData.basePrice}
              onChange={(e) => handleChange('basePrice', e.target.value)}
              className="flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
          {validationErrors.basePrice && (
            <p className="text-[10px] text-red-500 mt-1">Base price is required</p>
          )}
        </div>

        {/* Takeaway */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
            Takeaway
          </label>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg px-3 py-2.5 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-100 transition-all">
            <span className="text-slate-400 font-medium text-sm mr-2">₹</span>
            <input
              type="number"
              value={formData.takeawayPrice}
              onChange={(e) => handleChange('takeawayPrice', e.target.value)}
              className="flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
              placeholder={formData.basePrice ? `${formData.basePrice} (same as dine-in)` : 'Same as dine-in'}
              min="0"
              step="0.01"
            />
          </div>
        </div>

        {/* Delivery */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
            Delivery
          </label>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg px-3 py-2.5 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-100 transition-all">
            <span className="text-slate-400 font-medium text-sm mr-2">₹</span>
            <input
              type="number"
              value={formData.deliveryPrice}
              onChange={(e) => handleChange('deliveryPrice', e.target.value)}
              className="flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
              placeholder={formData.basePrice ? `${formData.basePrice} (same as dine-in)` : 'Same as dine-in'}
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <p className="text-[10px] text-slate-400 pt-1">
          Leave takeaway/delivery empty to use the dine-in price.
        </p>
      </div>

      {/* ── Right: Tax & HSN ── */}
      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pb-2 border-b border-slate-200">
          Tax & HSN
        </h3>

        {/* Effective Tax Info Banner */}
        {item && formData.effectiveTaxName && (
          <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-blue-700 leading-relaxed">
              <span className="font-semibold">Effective:</span>{" "}
              {formData.effectiveTaxName} ({formData.effectiveTaxRate}%)
              {formData.effectiveTaxCgstRate && formData.effectiveTaxSgstRate && (
                <span className="text-blue-600">
                  {" "}[CGST {formData.effectiveTaxCgstRate}% + SGST {formData.effectiveTaxSgstRate}%]
                </span>
              )}
              <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-medium uppercase">
                {formData.taxSource === "ITEM" ? "Item Override" :
                 formData.taxSource === "CATEGORY" ? "From Category" : "Default"}
              </span>
            </div>
          </div>
        )}

        {/* HSN/SAC Code */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
            HSN/SAC Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            data-field="hsn"
            value={formData.hsn}
            onChange={(e) => handleChange('hsn', e.target.value)}
            className={inputClass('hsn')}
            placeholder="996331"
            maxLength="8"
          />
          <p className="text-[10px] text-slate-400 mt-1">
            996331 — Restaurant services
          </p>
        </div>

        {/* Tax Override */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">
            Tax Override
          </label>
          <select
            value={formData.taxId || ""}
            onChange={(e) => handleChange('taxId', e.target.value ? Number(e.target.value) : null)}
            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
          >
            <option value="">Use category / default tax</option>
            {activeTaxes.map((tax) => (
              <option key={tax.id} value={tax.id}>
                {tax.name} ({tax.rate}%)
                {tax.centerGstRate && tax.stateGstRate
                  ? ` [CGST ${tax.centerGstRate}% + SGST ${tax.stateGstRate}%]`
                  : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Tax Inclusive */}
        <label className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors border border-slate-200">
          <input
            type="checkbox"
            checked={formData.inclusiveTaxRate !== false}
            onChange={(e) => handleChange('inclusiveTaxRate', e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-2 focus:ring-teal-200 focus:ring-offset-0 cursor-pointer"
          />
          <span className="text-sm font-medium text-slate-700">Tax Inclusive Price</span>
        </label>
      </div>
    </div>
  );

  const renderOptionsTab = () => (
    <div className="space-y-6">

      {/* Open Item Section */}
      <div className="p-4 bg-amber-50/50 border border-amber-200 rounded-xl space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Open Item</span>
          <span className="text-xs text-amber-600">Price entered at POS</span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <label className="flex items-center gap-2 p-2.5 bg-white rounded-lg cursor-pointer hover:bg-amber-50 transition-colors border border-amber-200 w-full">
            <input
              type="checkbox"
              checked={formData.isOpenItem}
              onChange={(e) => {
                handleChange('isOpenItem', e.target.checked);
                // Reset name editable when open item is toggled off
                if (!e.target.checked) handleChange('isNameEditable', false);
              }}
              className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-2 focus:ring-amber-200 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-sm font-medium text-slate-700">Mark as Open Item</span>
          </label>
          <label className={`flex items-center gap-2 p-2.5 bg-white rounded-lg border border-amber-200 w-full transition-colors ${formData.isOpenItem ? 'cursor-pointer hover:bg-amber-50' : 'opacity-50 cursor-not-allowed'}`}>
            <input
              type="checkbox"
              checked={formData.isNameEditable}
              onChange={(e) => handleChange('isNameEditable', e.target.checked)}
              disabled={!formData.isOpenItem}
              className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-2 focus:ring-amber-200 focus:ring-offset-0 cursor-pointer disabled:cursor-not-allowed"
            />
            <span className="text-sm font-medium text-slate-700">Name Editable at POS</span>
          </label>
        </div>
        {formData.isOpenItem && (
          <p className="text-xs text-amber-600">
            Staff will enter the price when adding this item on POS.
            {formData.isNameEditable && ' They can also enter a custom name.'}
          </p>
        )}
      </div>

      {/* Finished Item Section */}
      <div className="p-4 bg-teal-50/50 border border-teal-200 rounded-xl space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-teal-700 uppercase tracking-wide">Finished Item</span>
          <span className="text-xs text-teal-600">Direct stock deduction, no recipe needed</span>
        </div>
        <label className="flex items-center gap-2 p-2.5 bg-white rounded-lg cursor-pointer hover:bg-teal-50 transition-colors border border-teal-200 w-full">
          <input
            type="checkbox"
            checked={formData.isFinishedItem}
            onChange={(e) => handleChange('isFinishedItem', e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-2 focus:ring-teal-200 focus:ring-offset-0 cursor-pointer"
          />
          <span className="text-sm font-medium text-slate-700">Mark as Finished Item</span>
        </label>
        {formData.isFinishedItem && (
          <p className="text-xs text-teal-600">
            This item will be sold as-is (e.g., bottled drinks, packaged snacks). Stock is deducted directly from inventory — no recipe required.
            Use "Import from Menu" in Inventory to create the matching inventory item.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Status */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Status
          </label>
          <select
            value={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.value === 'true')}
            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        {/* Shortcut Key */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Shortcut Key
          </label>
          <input
            type="text"
            value={formData.shortcutKey}
            onChange={(e) => handleChange('shortcutKey', e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
            placeholder="e.g., 1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Veg/Non-Veg Toggle */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Food Type
          </label>
          <div className="flex items-center gap-4 bg-slate-50 px-4 py-2.5 rounded-lg border border-slate-200">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                checked={formData.isVeg}
                onChange={() => handleChange('isVeg', true)}
                className="w-4 h-4 text-green-600 focus:ring-green-500"
              />
              <span className="flex items-center gap-1 text-xs font-medium">
                <span className="w-3.5 h-3.5 border-2 border-green-600 rounded flex items-center justify-center bg-green-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-600"></span>
                </span>
                Veg
              </span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                checked={!formData.isVeg}
                onChange={() => handleChange('isVeg', false)}
                className="w-4 h-4 text-red-600 focus:ring-red-500"
              />
              <span className="flex items-center gap-1 text-xs font-medium">
                <span className="w-3.5 h-3.5 border-2 border-red-600 rounded flex items-center justify-center bg-red-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                </span>
                Non-Veg
              </span>
            </label>
          </div>
        </div>

        {/* Favorite */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Favorite
          </label>
          <label className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors border border-slate-200 w-full">
            <input
              type="checkbox"
              checked={formData.isFavorite}
              onChange={(e) => handleChange('isFavorite', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-2 focus:ring-amber-200 focus:ring-offset-0 cursor-pointer"
            />
            <Star size={16} className={formData.isFavorite ? 'text-amber-500 fill-amber-500' : 'text-slate-400'} />
            <span className="text-sm font-medium text-slate-700">Mark as Favorite</span>
          </label>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
            Topping
          </label>
          <label className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors border border-slate-200 w-full">
            <input
              type="checkbox"
              checked={formData.isTopping}
              onChange={(e) => handleChange('isTopping', e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-2 focus:ring-teal-200 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-sm font-medium text-slate-700">Mark as Topping</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderVariantsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Add different sizes or variations with service-mode pricing</p>
        <button
          type="button"
          onClick={handleAddVariant}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 text-xs font-semibold"
        >
          <Plus size={14} /> Add Variant
        </button>
      </div>

      {formData.variants.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <p className="text-sm">No variants added yet</p>
          <p className="text-xs mt-1">Click "Add Variant" to create size or type variations</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[2.5rem_1fr_6rem_6rem_6rem_2.5rem] gap-0 bg-slate-100 border-b border-slate-200">
            <div className="px-2 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center">#</div>
            <div className="px-3 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Variant Name</div>
            <div className="px-2 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center">Dine-In</div>
            <div className="px-2 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center">Takeaway</div>
            <div className="px-2 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center">Delivery</div>
            <div></div>
          </div>

          {/* Variant Rows */}
          {formData.variants.map((variant, index) => (
            <div
              key={variant.tempId}
              className={`grid grid-cols-[2.5rem_1fr_6rem_6rem_6rem_2.5rem] gap-0 items-center ${
                index < formData.variants.length - 1 ? 'border-b border-slate-100' : ''
              } hover:bg-slate-50/50 transition-colors`}
            >
              {/* Row Number */}
              <div className="px-2 py-2.5 text-center">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-slate-200 text-slate-600 rounded text-xs font-bold">
                  {index + 1}
                </span>
              </div>

              {/* Name */}
              <div className="px-2 py-2">
                <input
                  type="text"
                  value={variant.variantName}
                  onChange={(e) => handleVariantChange(variant.tempId, 'variantName', e.target.value)}
                  placeholder="e.g., Full, Half"
                  className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-100 transition-all"
                />
              </div>

              {/* Dine-In Price */}
              <div className="px-1.5 py-2">
                <div className="flex items-center bg-white border border-slate-200 rounded px-2 py-1.5 focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-100">
                  <span className="text-slate-400 text-xs mr-1">₹</span>
                  <input
                    type="number"
                    value={variant.variantPrice}
                    onChange={(e) => handleVariantChange(variant.tempId, 'variantPrice', e.target.value)}
                    placeholder="0"
                    className="w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Takeaway Price */}
              <div className="px-1.5 py-2">
                <div className="flex items-center bg-white border border-slate-200 rounded px-2 py-1.5 focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-100">
                  <span className="text-slate-400 text-xs mr-1">₹</span>
                  <input
                    type="number"
                    value={variant.takeawayPrice}
                    onChange={(e) => handleVariantChange(variant.tempId, 'takeawayPrice', e.target.value)}
                    placeholder={variant.variantPrice || '—'}
                    className="w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Delivery Price */}
              <div className="px-1.5 py-2">
                <div className="flex items-center bg-white border border-slate-200 rounded px-2 py-1.5 focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-100">
                  <span className="text-slate-400 text-xs mr-1">₹</span>
                  <input
                    type="number"
                    value={variant.deliveryPrice}
                    onChange={(e) => handleVariantChange(variant.tempId, 'deliveryPrice', e.target.value)}
                    placeholder={variant.variantPrice || '—'}
                    className="w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Remove */}
              <div className="px-1 py-2 text-center">
                <button
                  type="button"
                  onClick={() => handleRemoveVariant(variant.tempId)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Remove variant"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formData.variants.length > 0 && (
        <p className="text-[10px] text-slate-400">
          Leave takeaway/delivery empty to use the dine-in price for that variant.
        </p>
      )}
    </div>
  );

  const renderImageTab = () => (
    <div className="space-y-6">
      {/* Image Preview */}
      <div className="relative border-2 border-dashed border-slate-300 rounded-xl overflow-hidden bg-slate-50 max-w-xs mx-auto aspect-square">
        {imagePreview ? (
          <>
            <img
              src={imagePreview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
              title="Remove image"
            >
              <X size={16} />
            </button>
          </>
        ) : (
          <label className="flex flex-col items-center justify-center h-full cursor-pointer hover:bg-slate-100 transition-colors">
            <ImageIcon className="w-16 h-16 text-slate-400 mb-3" />
            <p className="text-sm font-medium text-slate-600">Click to select image</p>
            <p className="text-xs text-slate-500 mt-1">PNG, JPG, WebP, SVG & more — up to 5MB</p>
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/bmp,image/webp,image/svg+xml,image/tiff,image/x-icon,image/heic,image/heif,image/avif"
              onChange={handleImageSelect}
              className="hidden"
            />
          </label>
        )}
      </div>

      {/* Upload Button */}
      {imageFile && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleImageUpload}
            disabled={uploadingImage || !item?.id}
            className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadingImage ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} />
                Upload Image
              </>
            )}
          </button>
        </div>
      )}

      {!item?.id && imageFile && (
        <p className="text-xs text-amber-600 text-center bg-amber-50 p-2 rounded-lg border border-amber-200">
          Save the menu item first to upload the image
        </p>
      )}

      <p className="text-xs text-slate-500 text-center">
        {item?.id ? 'Select and upload a high-quality image' : 'Save the item first, then upload an image'}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-50 rounded-lg">
              <Plus className="w-5 h-5 text-slate-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                {item ? 'Edit Menu Item' : 'Add Menu Item'}
              </h2>
              <p className="text-xs text-slate-500">
                Configure your menu item details
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-50 rounded-lg transition-colors group"
          >
            <X className="w-5 h-5 text-slate-600 group-hover:text-slate-800" />
          </button>
        </div>

        {/* Tab Strip */}
        <div className="border-b border-slate-200 px-6">
          <nav className="flex gap-6 -mb-px">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'text-teal-600 border-teal-600'
                    : 'text-slate-500 border-transparent hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto flex flex-col">
          <div className="p-6 flex-1">
            {activeTab === 'details' && renderDetailsTab()}
            {activeTab === 'tax' && renderTaxTab()}
            {activeTab === 'options' && renderOptionsTab()}
            {activeTab === 'variants' && renderVariantsTab()}
            {activeTab === 'image' && renderImageTab()}
            {/* BulkPriceEditor tab removed. Now available in Menu Management modal. */}
          </div>

          {/* Footer */}
          <div className="bg-slate-50 border-t border-slate-100 px-6 py-4 flex items-center gap-3">
            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={fillTestData}
                className="px-4 py-2.5 bg-amber-50 border border-amber-300 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100 transition-all"
              >
                Fill Test Data
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-all flex items-center gap-2 shadow-sm"
            >
              <Check size={16} />
              {item ? 'Update Item' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
